import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import ListingCard from '@/components/ListingCard';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Star, MapPin, Calendar, MessageSquare, Edit2, Flag, User, Loader2, Save
} from 'lucide-react';

export default function ProfilePage() {
  const { userId } = useParams();
  const { user: currentUser, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [listings, setListings] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const isOwn = currentUser && currentUser.user_id === userId;

  useEffect(() => {
    Promise.all([
      api.get(`/users/${userId}`),
      api.get(`/listings?limit=100`).then(r => r.data.listings.filter(l => l.user_id === userId)),
      api.get(`/reviews/${userId}`),
    ]).then(([userRes, userListings, reviewsRes]) => {
      setProfile(userRes.data);
      setListings(userListings);
      setReviews(reviewsRes.data);
      setEditForm({ name: userRes.data.name, location: userRes.data.location, bio: userRes.data.bio, phone: userRes.data.phone });
    }).catch(() => {}).finally(() => setLoading(false));
  }, [userId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await api.put('/users/profile', editForm);
      setProfile(data);
      setEditing(false);
      refreshUser();
      toast.success('Profile updated');
    } catch {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleReview = async () => {
    setSubmittingReview(true);
    try {
      await api.post('/reviews', { reviewed_user_id: userId, rating: reviewForm.rating, comment: reviewForm.comment });
      const { data } = await api.get(`/reviews/${userId}`);
      setReviews(data);
      const { data: updatedUser } = await api.get(`/users/${userId}`);
      setProfile(updatedUser);
      setReviewForm({ rating: 5, comment: '' });
      toast.success('Review submitted');
    } catch {
      toast.error('Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!profile) return <div className="text-center py-20 text-muted-foreground">User not found</div>;

  return (
    <div className="min-h-screen" data-testid="profile-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        {/* Profile Header */}
        <div className="bg-white border border-[#E8E6DF] rounded-2xl p-6 mb-8">
          <div className="flex flex-col sm:flex-row items-start gap-6">
            <div className="w-20 h-20 rounded-2xl bg-muted flex items-center justify-center overflow-hidden flex-shrink-0">
              {profile.picture ? (
                <img src={profile.picture} alt="" className="w-full h-full object-cover" />
              ) : (
                <User className="w-10 h-10 text-muted-foreground" strokeWidth={1.5} />
              )}
            </div>
            <div className="flex-1 w-full">
              {editing ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs text-muted-foreground">Name</Label>
                      <Input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} className="rounded-xl bg-[#FDFBF7] border-[#E8E6DF] h-10" data-testid="edit-name-input" />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Location</Label>
                      <Input value={editForm.location} onChange={e => setEditForm({ ...editForm, location: e.target.value })} className="rounded-xl bg-[#FDFBF7] border-[#E8E6DF] h-10" data-testid="edit-location-input" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Bio</Label>
                    <Textarea value={editForm.bio} onChange={e => setEditForm({ ...editForm, bio: e.target.value })} className="rounded-xl bg-[#FDFBF7] border-[#E8E6DF]" data-testid="edit-bio-input" />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Phone</Label>
                    <Input value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} className="rounded-xl bg-[#FDFBF7] border-[#E8E6DF] h-10" data-testid="edit-phone-input" />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={handleSave} disabled={saving} className="rounded-xl bg-primary text-white gap-1.5" data-testid="save-profile-btn">
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.5} />}Save
                    </Button>
                    <Button variant="ghost" onClick={() => setEditing(false)} className="rounded-xl" data-testid="cancel-edit-btn">Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-start justify-between">
                    <div>
                      <h1 className="text-2xl font-bold text-[#2C362F]" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="profile-name">{profile.name || 'User'}</h1>
                      <div className="flex flex-wrap items-center gap-3 mt-1 text-sm text-muted-foreground">
                        {profile.location && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" strokeWidth={1.5} />{profile.location}</span>}
                        <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" strokeWidth={1.5} />Joined {new Date(profile.created_at).toLocaleDateString()}</span>
                        {profile.rating_count > 0 && (
                          <span className="flex items-center gap-1">
                            <Star className="w-3.5 h-3.5 text-[#E5B66A] fill-[#E5B66A]" />{profile.rating_avg} ({profile.rating_count} reviews)
                          </span>
                        )}
                      </div>
                    </div>
                    {isOwn && (
                      <Button variant="outline" size="sm" className="rounded-xl gap-1.5 border-[#E8E6DF]" onClick={() => setEditing(true)} data-testid="edit-profile-btn">
                        <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} />Edit
                      </Button>
                    )}
                  </div>
                  {profile.bio && <p className="mt-3 text-sm text-foreground/80 leading-relaxed">{profile.bio}</p>}
                </>
              )}
            </div>
          </div>
        </div>

        {/* Listings */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-[#2C362F] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Listings ({listings.length})
          </h2>
          {listings.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.map(l => <ListingCard key={l.listing_id} listing={l} />)}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground bg-white border border-[#E8E6DF] rounded-2xl p-6 text-center">No listings yet</p>
          )}
        </div>

        {/* Reviews */}
        <div>
          <h2 className="text-xl font-semibold text-[#2C362F] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Reviews ({reviews.length})
          </h2>

          {currentUser && currentUser.user_id && !isOwn && (
            <div className="bg-white border border-[#E8E6DF] rounded-2xl p-6 mb-4" data-testid="review-form">
              <h3 className="text-sm font-semibold mb-3">Leave a Review</h3>
              <div className="flex items-center gap-1 mb-3">
                {[1, 2, 3, 4, 5].map(n => (
                  <button key={n} onClick={() => setReviewForm({ ...reviewForm, rating: n })} data-testid={`rating-star-${n}`}>
                    <Star className={`w-6 h-6 transition-colors ${n <= reviewForm.rating ? 'text-[#E5B66A] fill-[#E5B66A]' : 'text-[#E8E6DF]'}`} />
                  </button>
                ))}
              </div>
              <Textarea
                value={reviewForm.comment}
                onChange={e => setReviewForm({ ...reviewForm, comment: e.target.value })}
                placeholder="Share your experience..."
                className="rounded-xl bg-[#FDFBF7] border-[#E8E6DF] mb-3"
                data-testid="review-comment-input"
              />
              <Button onClick={handleReview} disabled={submittingReview} className="rounded-xl bg-primary text-white" data-testid="submit-review-btn">
                {submittingReview ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Submit Review'}
              </Button>
            </div>
          )}

          {reviews.length > 0 ? (
            <div className="space-y-3">
              {reviews.map(r => (
                <div key={r.review_id} className="bg-white border border-[#E8E6DF] rounded-2xl p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center overflow-hidden">
                      {r.reviewer_picture ? <img src={r.reviewer_picture} alt="" className="w-full h-full object-cover" /> : <User className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />}
                    </div>
                    <span className="font-medium text-sm">{r.reviewer_name || 'User'}</span>
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map(n => (
                        <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? 'text-[#E5B66A] fill-[#E5B66A]' : 'text-[#E8E6DF]'}`} />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground ml-auto">{new Date(r.created_at).toLocaleDateString()}</span>
                  </div>
                  {r.comment && <p className="text-sm text-foreground/80">{r.comment}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground bg-white border border-[#E8E6DF] rounded-2xl p-6 text-center">No reviews yet</p>
          )}
        </div>
      </div>
    </div>
  );
}
